import { stripHtml } from "./helpers";

/**
 * Extracts fragment count from a gross description HTML string.
 *
 * Patterns derived from all known gross code templates:
 *
 * | Code       | Text pattern                                          | Result |
 * |------------|-------------------------------------------------------|--------|
 * | F          | "are {{fragments}} fragments of tan soft tissue"      | N      |
 * | TV         | "are {{tissueFragments}} fragments of tan soft tissue" | N     |
 * | F1         | "is a single fragment of tan soft tissue"              | 1      |
 * | LP,PB,PNS, | "is a single polypoid fragment"                       | 1      |
 * | PSS,PT     |                                                       |        |
 * | INS        | "does not reveal any grossly identifiable tissue"     | 0      |
 * | MNS        | "no grossly identifiable tissue fragments"            | 0      |
 *
 * Also resolves TipTap variable chip spans (data-variable-value attributes)
 * before stripping HTML, so template variables rendered as chips are included.
 */
export function extractFragmentCountFromDescription(
  grossHtml: string
): number | null {
  if (!grossHtml) return null;

  // Resolve variable chip spans: replace <span data-variable-value="5">{{fragments}}</span>
  // with the actual value before stripping HTML
  const resolvedHtml = grossHtml.replace(
    /<span[^>]*data-variable-value="([^"]*)"[^>]*>.*?<\/span>/gi,
    "$1"
  );

  const text = stripHtml(resolvedHtml);
  if (!text) return null;

  // Pattern A: Explicit number before "fragment(s)"
  // Matches: "are 5 fragments of tan soft tissue", "3 fragments of vegetable matter"
  // Covers: F, TV templates
  const numberMatch = text.match(/(\d+)\s+fragments?\b/i);
  if (numberMatch) {
    const count = parseInt(numberMatch[1], 10);
    if (!isNaN(count) && count >= 0) return count;
  }

  // Pattern B: "a single fragment" or "a single polypoid fragment"
  // Covers: F1, LP, PB, PNS, PSS, PT templates
  if (/a\s+single\s+(?:polypoid\s+)?fragment/i.test(text)) {
    return 1;
  }

  // Pattern C: No identifiable tissue — 0 fragments
  // INS: "does not reveal any grossly identifiable tissue"
  // MNS: "no grossly identifiable tissue fragments"
  if (/grossly\s+identifiable\s+tissue/i.test(text)) {
    return 0;
  }

  return null;
}
